import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  fetchAdminTerminationConfirmationPdfUrl,
  fetchAdminTerminationConfirmations,
  generateAdminTerminationConfirmation,
} from "../../../../api/adminData";
import { SECONDARY_BUTTON } from "../../tenancies/workspaceUi";
import TerminationConfirmationDelivery from "./TerminationConfirmationDelivery";
import {
  currentVersionOf,
  deliveryListLabel,
} from "./terminationConfirmationDeliveryModel";
import {
  canOfferGeneration,
  confirmationPanelView,
  generatedAtText,
  otherVersions,
  pdfButtonLabel,
  readinessMessages,
  versionLine,
} from "./terminationConfirmationModel";

// Foreground text with a destructive marker — the termination block's own.
const LOAD_ERROR =
  "m-0 border-l-2 border-destructive pl-2 text-[12.5px] text-foreground";
const BADGE =
  "rounded-full border border-border bg-muted px-2 py-[1px] text-[11px] text-muted-foreground";
// "Veraltet" is told apart by its word and a stronger outline, not by a colour.
const STALE_BADGE =
  "rounded-full border border-foreground/50 bg-card px-2 py-[1px] text-[11px] font-medium text-foreground";
const ACTIONS = "flex flex-wrap items-center gap-2";

/**
 * KND-3c — "Kündigungsbestätigung", inside the termination block of one
 * tenancy (rendered by `TenancyTerminationSection` once a termination record
 * exists; never a second termination UI).
 *
 * THE SERVER DECIDES. Readiness, the current version, "stale" and every
 * version's lifecycle come from `GET …/termination/confirmations`; this panel
 * only shows them (`terminationConfirmationModel`). No object key, no
 * snapshot, no date arithmetic.
 *
 * REQUESTS. One GET when it mounts, and one more each time `refreshToken`
 * changes (the block bumps it after a save or a withdrawal — a save may have
 * generated the first version on the server). A generation is the POST plus
 * one GET for the new history; a refused one (409 / 422: the server state
 * moved) one GET as well. A PDF link is fetched only on click and never kept.
 * Nothing polls.
 *
 * PESSIMISTIC. The generate button is disabled while its request runs, so a
 * double click is one request; the state changes only on the server's answer.
 *
 * NO PARENT RELOAD. A generation changes nothing the page shows outside this
 * panel, so it reaches nothing outside it: no `onChanged`, no tenant or
 * tenancy refetch. The audit entry the server wrote is persisted and appears
 * in the Verlauf on its next regular refresh.
 *
 * KND-4b. The e-mail part of the current version is its own container,
 * `TerminationConfirmationDelivery` (Regel 48): it reads the version's
 * delivery history from this panel's answer, asks for the preview only on a
 * click, and refreshes through this panel's `load` — one GET after a send,
 * nothing outside the panel. Earlier versions show their delivery state as
 * text and offer no action.
 */
export default function TerminationConfirmationPanel({
  tenancyId,
  refreshToken = 0,
  canGenerate = true,
}) {
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  // A request that answers after the panel was closed must not set state.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The newest GET wins; an older answer arriving late is dropped.
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!tenancyId) return;
    const request = ++requestRef.current;
    const latest = () => mountedRef.current && request === requestRef.current;
    setRefreshing(true);
    try {
      const next = await fetchAdminTerminationConfirmations(tenancyId);
      if (!latest()) return;
      setData(next);
      setLoadState("ready");
    } catch {
      if (!latest()) return;
      setLoadState("error");
    } finally {
      if (latest()) setRefreshing(false);
    }
  }, [tenancyId]);

  // Another tenancy's answer must never stand in for this one's.
  useEffect(() => {
    setData(null);
    setLoadState("loading");
  }, [tenancyId]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const handleGenerate = async () => {
    if (generating || !tenancyId) return;
    setGenerating(true);
    let created = null;
    try {
      created = await generateAdminTerminationConfirmation(tenancyId);
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(err?.message || "Kündigungsbestätigung konnte nicht erstellt werden.");
      // A refusal means the server sees something else than this panel shows.
      if (err?.status === 409 || err?.status === 422) await load();
      if (mountedRef.current) setGenerating(false);
      return;
    }
    // Created. From here on nothing may report the generation as failed.
    // The one GET below is the whole refresh — nothing outside the panel.
    const version = created?.confirmation?.version;
    if (!mountedRef.current) return;
    toast.success(
      version
        ? `Kündigungsbestätigung v${version} erstellt.`
        : "Kündigungsbestätigung erstellt."
    );
    await load();
    if (mountedRef.current) setGenerating(false);
  };

  const handleOpenPdf = async (version) => {
    if (!version?.id || openingId) return;
    // Open the tab SYNCHRONOUSLY, before awaiting, so the browser does not
    // block it as a programmatic popup (the rental contract's convention). No
    // "noopener" here: it can make window.open return null and break the
    // open-then-navigate flow; the opener is severed by hand instead.
    const pdfTab = window.open("", "_blank");
    if (pdfTab) {
      try {
        pdfTab.opener = null;
      } catch (_) {
        /* read-only opener — ignore */
      }
    }
    setOpeningId(version.id);
    try {
      const result = await fetchAdminTerminationConfirmationPdfUrl(version.id);
      if (!result?.url) {
        pdfTab?.close();
        toast.error("PDF-Link konnte nicht geladen werden.");
      } else if (pdfTab) {
        pdfTab.location = result.url;
      } else {
        toast.error("Popup wurde blockiert. Bitte Popups erlauben.");
      }
    } catch (err) {
      pdfTab?.close();
      toast.error(err?.message || "PDF-Link konnte nicht geladen werden.");
    } finally {
      if (mountedRef.current) setOpeningId(null);
    }
  };

  if (!tenancyId) return null;

  const view = loadState === "ready" ? confirmationPanelView(data) : null;
  if (view === "hidden") return null;

  const pdfButton = (version) => (
    <button
      type="button"
      className={SECONDARY_BUTTON}
      onClick={() => handleOpenPdf(version)}
      disabled={openingId === version.id}
      aria-busy={openingId === version.id}
      aria-label={pdfButtonLabel(version)}
    >
      PDF öffnen
    </button>
  );

  const generateButton = (label) => (
    <button
      type="button"
      className={SECONDARY_BUTTON}
      onClick={handleGenerate}
      disabled={generating}
      aria-busy={generating}
    >
      {generating ? "Wird erstellt …" : label}
    </button>
  );

  const issues = readinessMessages(data);
  const issueList = issues.length ? (
    <ul className="m-0 list-disc pl-5 text-[12px] leading-snug text-muted-foreground">
      {issues.map((message, index) => (
        <li key={`${index}-${message}`}>{message}</li>
      ))}
    </ul>
  ) : null;

  const current = data?.current_confirmation || null;
  const offerGeneration = canGenerate && canOfferGeneration(view, data);

  let body;
  if (loadState === "loading") {
    body = (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Kündigungsbestätigung wird geladen …
      </p>
    );
  } else if (loadState === "error" || view === "unknown") {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className={LOAD_ERROR}>
          Kündigungsbestätigung konnte nicht geladen werden.
        </p>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={load}
          disabled={refreshing}
          aria-busy={refreshing}
        >
          Erneut laden
        </button>
      </div>
    );
  } else if (view === "not_created") {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="m-0 text-[12.5px] text-foreground">
          Kündigungsbestätigung noch nicht erstellt
        </p>
        {offerGeneration ? generateButton("Bestätigung erstellen") : null}
      </div>
    );
  } else if (view === "not_ready") {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="m-0 text-[12.5px] font-medium text-foreground">
          Bestätigung kann noch nicht erstellt werden
        </p>
        {issueList}
      </div>
    );
  } else if (view === "current" || view === "stale") {
    const stale = view === "stale";
    body = (
      <div className="flex flex-col items-start gap-2">
        <div className={ACTIONS}>
          <span className="text-[12.5px] font-medium text-foreground">
            {`Version ${current.version}`}
          </span>
          <span className={stale ? STALE_BADGE : BADGE}>
            {stale ? "Veraltet" : "Aktuell"}
          </span>
        </div>
        <p className="m-0 text-[12px] text-muted-foreground">
          {generatedAtText(current)}
        </p>
        {stale ? (
          <p className="m-0 text-[12.5px] leading-snug text-foreground">
            Die Daten der Kündigung haben sich seit der letzten Erstellung
            geändert.
          </p>
        ) : null}
        {stale && !canOfferGeneration(view, data) ? (
          <>
            <p className="m-0 text-[12.5px] font-medium text-foreground">
              Neue Version kann noch nicht erstellt werden
            </p>
            {issueList}
          </>
        ) : null}
        <div className={ACTIONS}>
          {stale && offerGeneration ? generateButton("Neue Version erstellen") : null}
          {pdfButton(current)}
        </div>
        <TerminationConfirmationDelivery
          version={currentVersionOf(data)}
          stale={stale}
          canAct={canGenerate}
          onRefresh={load}
        />
      </div>
    );
  } else {
    body = null; // history_only: the list below is all there is
  }

  const others = loadState === "ready" && view !== "unknown" ? otherVersions(data) : [];
  const historyId = `termination-confirmation-history-${tenancyId}`;
  const headingId = `termination-confirmation-heading-${tenancyId}`;

  return (
    <section
      aria-labelledby={headingId}
      aria-busy={refreshing || generating}
      data-testid={`termination-confirmation-${tenancyId}`}
      className="mt-4 border-t border-border pt-4"
    >
      <h5
        id={headingId}
        className="m-0 mb-2 text-[12.5px] font-semibold text-foreground"
      >
        Kündigungsbestätigung
      </h5>
      {body}
      {others.length ? (
        <div className={body ? "mt-3" : ""}>
          <p
            id={historyId}
            className="m-0 mb-1 text-[11.5px] leading-tight text-muted-foreground"
          >
            {view === "history_only" ? "Versionen" : "Frühere Versionen"}
          </p>
          <ul
            aria-labelledby={historyId}
            className="m-0 flex list-none flex-col gap-1 p-0"
            data-testid={historyId}
          >
            {others.map((version) => (
              <li
                key={version.id}
                className="flex min-w-0 flex-wrap items-center justify-between gap-2"
              >
                <span className="min-w-0 break-words text-[12px] text-foreground">
                  {[versionLine(version), deliveryListLabel(version, { withNone: false })]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {pdfButton(version)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
